/**
 * StyleResolver — a pure style engine. It owns style storage, resolution and
 * dirty tracking for one node's schema. It knows nothing about Konva, layers,
 * history or redraw — those are the owning Node's responsibility (see
 * ObjectGroup.refreshStyle()), which is what actually happens after a style
 * changes:
 *
 *   node.setStyle(...) / node.setStateStyle(...)
 *           |
 *           v
 *   StyleResolver.setStyleProp(...) / setStateStyleProp(...)  -- write + dirty tracking, pure
 *           |
 *           v
 *   StyleResolver.markDirty(...)
 *           |
 *           v
 *   Node.refreshStyle(...)            -- history, events, redraw (not here)
 *
 * Modeled on LVGL 9's style cascade: a part's effective style is resolved by
 * walking an ordered list of StyleSources (Widget, Theme, Local, State), each
 * contributing a partial PartStyle for a given part/state; later sources in
 * the list win over earlier ones for whichever properties they define.
 *
 * This is the SINGLE, canonical implementation of that cascade. No other
 * module (renderer, exporter, validator, live-preview, Property Editor,
 * automation) may compare a value against a widget default, theme default or
 * state override on its own — everyone calls through here.
 *
 * Every source is a stateless function of the context it's given
 * (schema/theme/part/state) — none of them caches or stores a theme
 * reference between calls. The one genuinely mutable piece of state in the
 * whole system is the node's own local override storage (node.parts),
 * wrapped by LocalStyleSource/StateStyleSource; theme data is always read
 * fresh from the `theme` argument passed in by the caller and never written to.
 *
 * node.parts storage shape (sparse — holds ONLY explicitly-set values;
 * absence means "inherit", resolved live through the cascade, never baked in):
 *
 *   node.parts = {
 *     main: {
 *       states: {
 *         default: { properties: { bg_color: '#333333' } },
 *         checked: { properties: { bg_color: '#00ff00' } },
 *       },
 *     },
 *   }
 *
 * node.parts is intentionally NOT public API for anything outside this file
 * and ConfigManager.js. Widgets, the Property Editor and the exporter
 * read/write through the Node (node.style/node.setStyle/node.setStateStyle,
 * see ObjectGroup.js) — never node.parts directly, and never this module's
 * internals directly.
 */

import { stateSuffix } from './stateFormat.js'
import { fromWireKey } from './wireFormat.js'

// ---------------------------------------------------------------------------
// StyleSource — common interface: resolvePartStyle(part, ctx) -> partial
// PartStyle, where ctx = { schema, theme, state }. Return {} (or omit a
// property) to mean "no opinion, defer to the next source". Sources never
// store ctx between calls.
// ---------------------------------------------------------------------------

class StyleSource {
    resolvePartStyle(_part, _ctx) {
        return {}
    }

    /** True when this source has an explicit stored value for prop (vs. merely resolving one). Default: never explicit. */
    hasExplicitValue(_part, _prop, _ctx) {
        return false
    }
}

/** Lowest priority: the widget type's own hardcoded baseline (schema.defaults.styles), never per-instance state. */
class WidgetDefaultStyleSource extends StyleSource {
    resolvePartStyle(part, { schema }) {
        return { ...(schema?.defaults?.styles?.[part] ?? {}) }
    }
}

/**
 * Theme defaults for this widget type. `ctx.theme` is the caller-supplied,
 * already-resolved theme slice for this object's type (e.g. the equivalent
 * of `getActiveTheme()[node.obj]`) — this source never looks up or caches a
 * theme itself, it only reads whatever it's handed (principle: stateless).
 *
 * Canonical shape for a non-default state is semantic and lives right next
 * to the part's default styles: `theme.styles.<part>.states.<state>` (plain
 * property names, e.g. `{ bg_color: '#00ff00' }` — same shape node.parts
 * already uses, see LocalStyleSource/StateStyleSource above). Bundled themes
 * are being migrated to this shape one at a time (see stateFormat.js).
 *
 * TRANSITIONAL: themes not yet migrated still carry their per-state
 * overrides in the legacy wire-suffixed form
 * (`theme.states.states01.bg_color10`, per-state group, wire-keyed within)
 * — see the theme builder files under properties/themes/. This source
 * translates that legacy data at read time using fromWireKey — the SAME
 * sanctioned wire-format parser the import/export boundary uses (see
 * wireFormat.js) — so suffix knowledge still lives in exactly one place.
 * When a theme defines the SAME property in both the legacy group and the
 * semantic block, the semantic value wins. Once every bundled theme is
 * migrated, the legacy branch (and the fromWireKey import below) is deleted
 * entirely — see the state-migration plan.
 */
class ThemeStyleSource extends StyleSource {
    resolvePartStyle(part, { theme, state, schema }) {
        const { states: statesByName, ...defaultStyles } = theme?.styles?.[part] ?? {}
        const result = { ...defaultStyles }
        if (state && state !== 'default') {
            if (theme?.states) {
                const groupKey = 'states' + stateSuffix(state)
                const group = theme.states[groupKey] ?? {}
                for (const [wireKey, value] of Object.entries(group)) {
                    const parsed = fromWireKey(wireKey, schema)
                    if (parsed.partName === part) result[parsed.propName] = value
                }
            }
            Object.assign(result, statesByName?.[state] ?? {})
        }
        return result
    }
}

/** Part default (the node's own explicit "default state" overrides) — the one part of the cascade below Theme that's genuinely mutable. */
class LocalStyleSource extends StyleSource {
    /** @param storage the plain node.parts object the owning Node keeps a reference to. */
    constructor(storage) {
        super()
        this.storage = storage
    }

    resolvePartStyle(part, _ctx) {
        return { ...(this.storage[part]?.states?.default?.properties ?? {}) }
    }

    hasExplicitValue(part, prop, _ctx) {
        return this.storage[part]?.states?.default?.properties?.[prop] !== undefined
    }

    /** The only writable source for the default state. Returns whether the value actually changed. */
    set(part, prop, value) {
        this.storage[part] ??= { states: {} }
        this.storage[part].states.default ??= { properties: {} }
        const bucket = this.storage[part].states.default.properties
        const changed = bucket[prop] !== value
        bucket[prop] = value
        return changed
    }

    /** Delete the explicit override, reverting to whatever the cascade beneath resolves to. Returns whether anything was actually removed. */
    reset(part, prop) {
        const bucket = this.storage[part]?.states?.default?.properties
        if (!bucket || !(prop in bucket)) return false
        delete bucket[prop]
        return true
    }
}

/** State override (checked/pressed/disabled/…) — same storage object as LocalStyleSource, a different sub-path; contributes nothing when resolving the 'default' state. */
class StateStyleSource extends StyleSource {
    /** @param storage the SAME plain node.parts object LocalStyleSource wraps. */
    constructor(storage) {
        super()
        this.storage = storage
    }

    resolvePartStyle(part, { state }) {
        if (!state || state === 'default') return {}
        return { ...(this.storage[part]?.states?.[state]?.properties ?? {}) }
    }

    hasExplicitValue(part, prop, { state }) {
        if (!state || state === 'default') return false
        return this.storage[part]?.states?.[state]?.properties?.[prop] !== undefined
    }

    /** The only writable source for non-default states. Returns whether the value actually changed. */
    set(part, state, prop, value) {
        this.storage[part] ??= { states: {} }
        this.storage[part].states[state] ??= { properties: {} }
        const bucket = this.storage[part].states[state].properties
        const changed = bucket[prop] !== value
        bucket[prop] = value
        return changed
    }

    /** Delete the explicit state override, reverting to the part's default state. Returns whether anything was actually removed. */
    reset(part, state, prop) {
        const bucket = this.storage[part]?.states?.[state]?.properties
        if (!bucket || !(prop in bucket)) return false
        delete bucket[prop]
        return true
    }
}

export { StyleSource, WidgetDefaultStyleSource, ThemeStyleSource, LocalStyleSource, StateStyleSource }

// ---------------------------------------------------------------------------
// StyleResolver
// ---------------------------------------------------------------------------

export default class StyleResolver {
    /**
     * @param schema the object's v2 schema (schema metadata only — never a node)
     * @param sources ordered array of exactly 4 StyleSource instances, fixed
     *   positions [0]=widget default, [1]=theme, [2]=local (default state),
     *   [3]=state override — resolveTrace()/hasExplicitValue()/the
     *   state-aware mutation methods below all index into this array
     *   positionally. Precedence increases with index (later wins).
     * @param localSource sources[2] — kept as a named field for readability at the call sites that only ever touch the default state.
     */
    constructor(schema, sources, localSource) {
        this.schema = schema
        this.sources = sources
        this.localSource = localSource
        this._cache = new Map() // "part:state" -> { theme, value: resolved PartStyle object }
    }

    _cacheKey(part, state) {
        return `${part}:${state}`
    }

    /**
     * Resolve the full effective style for a part/state. This is the primary,
     * preferred entry point — widgets should consume a whole PartStyle rather
     * than querying individual properties.
     *
     * @param theme the caller-supplied, already-resolved theme slice for this
     *   object's type (may be undefined/{} — the source treats that as "no
     *   theme opinion"). Never stored — passed fresh on every call.
     * @param state semantic state name (default: 'default').
     *
     * Returns a fresh plain object every call (never cached by identity) so
     * callers never hold on to a stale resolved value across a redraw.
     */
    style(part, { theme, state = 'default' } = {}) {
        const key = this._cacheKey(part, state)
        const cached = this._cache.get(key)
        if (cached && cached.theme === theme) return cached.value

        const ctx = { schema: this.schema, theme, state }
        const resolved = this.sources.reduce((acc, source) => Object.assign(acc, source.resolvePartStyle(part, ctx)), {})
        this._cache.set(key, { theme, value: resolved })
        return resolved
    }

    /**
     * Resolve a single property. Secondary API — for infrastructure that
     * genuinely needs one value at a time (Property Editor form fields,
     * the exporter), not for widget redraw code.
     */
    resolveStyleProp(part, prop, ctx = {}) {
        return this.style(part, ctx)[prop]
    }

    /**
     * Resolve part/prop/state through every cascade level and report which
     * level actually supplied the effective value, whether the requested
     * state itself had an explicit override, and whether that explicit
     * override is REDUNDANT — i.e. identical to what the cascade would have
     * produced without it. This is the ONE shared primitive every other
     * layer (exporter/optimizer, validator, property editor "is this
     * inherited?" indicator) must use instead of re-deriving comparisons
     * themselves — in particular, `redundant` is the export optimizer's
     * entire decision rule ("only emit values that change the effective
     * rendered result"), computed here so the optimizer never performs its
     * own default/inheritance comparison.
     *
     * @returns {{ value: *, source: 'widget'|'theme'|'local'|'state', explicit: boolean, inherited: boolean, redundant: boolean }}
     */
    resolveTrace(part, prop, { theme, state = 'default' } = {}) {
        const ctx = { schema: this.schema, theme, state }
        const levels = [
            { name: 'widget', source: this.sources[0] },
            { name: 'theme', source: this.sources[1] },
            { name: 'local', source: this.sources[2] },
            { name: 'state', source: this.sources[3] },
        ]

        let value
        let sourceName
        for (const { name, source } of levels) {
            const partial = source.resolvePartStyle(part, ctx)
            if (partial[prop] !== undefined) {
                value = partial[prop]
                sourceName = name
            }
        }

        // "explicit" means: the level matching the requested state actually
        // stored this value itself (not inherited from a lower level).
        const requestedLevelName = state === 'default' ? 'local' : 'state'
        const explicit = sourceName === requestedLevelName

        // "redundant" means: an explicit override exists, but resolving the
        // SAME part/prop with that one level's source removed from the
        // cascade (everything beneath it) produces the identical value — the
        // override changes nothing and can be safely omitted from export.
        // Default state's "beneath" is widget+theme; a non-default state's
        // "beneath" is the part's own resolved default state (widget+theme+local).
        // `belowValue` is exposed alongside `redundant` (a plain value
        // equality check) because a raw-value comparison isn't always the
        // right equivalence test for every property encoding (e.g. a color
        // property's explicit value may carry an embedded alpha suffix the
        // cascade level beneath it doesn't) — callers that need a
        // domain-aware equivalence check (still never re-deriving cascade
        // VALUES themselves) can use `belowValue` for that; they must not
        // resolve it any other way.
        let redundant = false
        let belowValue
        if (explicit) {
            const belowSources = state === 'default'
                ? [this.sources[0], this.sources[1]]
                : [this.sources[0], this.sources[1], this.sources[2]]
            belowValue = belowSources.reduce(
                (acc, source) => Object.assign(acc, source.resolvePartStyle(part, ctx)), {}
            )[prop]
            redundant = value === belowValue
        }

        return { value, source: sourceName, explicit, inherited: !explicit, redundant, belowValue }
    }

    /** Storage-presence check (not resolved value) for the requested state level — lets callers distinguish "inherited" from "explicit-and-happens-to-match". */
    hasExplicitValue(part, prop, { state = 'default' } = {}) {
        const source = state === 'default' ? this.sources[2] : this.sources[3]
        return source.hasExplicitValue(part, prop, { schema: this.schema, state })
    }

    /**
     * The sole mutation entry point for default-state style properties:
     * write + dirty tracking, nothing else. Returns whether the value
     * actually changed — the caller (the owning Node) decides what to do
     * about it.
     */
    setStyleProp(part, prop, value) {
        const changed = this.localSource.set(part, prop, value)
        if (changed) this.markDirty(part)
        return changed
    }

    /** Same as setStyleProp, but for an explicit non-default state override. Delegates to setStyleProp when state is 'default'. */
    setStateStyleProp(part, state, prop, value) {
        if (state === 'default') return this.setStyleProp(part, prop, value)
        const changed = this.sources[3].set(part, state, prop, value)
        if (changed) this.markDirty(part)
        return changed
    }

    /** Delete the default state's explicit override, reverting to theme/widget inheritance. Returns whether anything changed. */
    resetStyleProp(part, prop) {
        const changed = this.localSource.reset(part, prop)
        if (changed) this.markDirty(part)
        return changed
    }

    /** Delete a non-default state's explicit override, reverting to the part's default state. Delegates to resetStyleProp when state is 'default'. */
    resetStateStyleProp(part, state, prop) {
        if (state === 'default') return this.resetStyleProp(part, prop)
        const changed = this.sources[3].reset(part, state, prop)
        if (changed) this.markDirty(part)
        return changed
    }

    /**
     * Write without dirtying anything the caller needs to react to beyond
     * the cache — for transient UI feedback (e.g. live color-picker drag)
     * where the caller commits with setStyleProp once the interaction
     * finishes. Prefer setStyleProp for normal edits.
     */
    pokeStyle(part, prop, value) {
        this.localSource.set(part, prop, value)
        this.markDirty(part)
    }

    /** Drop every cached resolved style (all states) for a part, forcing the next style()/resolveStyleProp() to recompute. */
    markDirty(part) {
        const prefix = `${part}:`
        for (const key of this._cache.keys()) {
            if (key.startsWith(prefix)) this._cache.delete(key)
        }
    }
}
